import { FunctionComponent, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";

import { getComposedEventTarget } from "@/utils/getComposedEventTarget";

import { Email } from "@/pages/Private/pages/Account/schema/email";

const MENU_WIDTH = 280;
const MENU_GAP = 6;
const VIEWPORT_PADDING = 8;

/** Fixed-position coordinates that hang the menu under the anchor, kept inside the viewport. */
const positionBelow = (anchorEl: HTMLElement) => {
	const rect = anchorEl.getBoundingClientRect();
	const maxLeft = window.innerWidth - MENU_WIDTH - VIEWPORT_PADDING;

	return {
		top: rect.bottom + MENU_GAP,
		left: Math.max(VIEWPORT_PADDING, Math.min(rect.left, maxLeft)),
	};
};

interface SidebarEmailReconnectMenuProps {
	/** The warning chip the menu hangs under; its bounding rect drives the position. */
	anchorEl: HTMLElement;
	/** The logged-out mailboxes to pick from. */
	emails: Email[];
	onSelect: (emailId: number) => void;
	onClose: () => void;
	/** Portal target — defaults to `document.body` (web rule §11). */
	portalContainer?: HTMLElement | null;
}

/**
 * The "which mailbox?" picker the sidebar's email warning opens when more than one mailbox is
 * logged out. Portaled and `position: fixed` so the sidebar's `overflow-x-hidden` cannot clip it;
 * closes on outside click and Escape, re-anchors on scroll and resize, like the shared dropdowns.
 */
export const SidebarEmailReconnectMenu: FunctionComponent<SidebarEmailReconnectMenuProps> = ({
	anchorEl,
	emails,
	onSelect,
	onClose,
	portalContainer,
}) => {
	const { t } = useTranslation();
	const menuRef = useRef<HTMLDivElement>(null);
	// The menu mounts fresh on every open, so the first position is computed synchronously and
	// only scroll / resize move it afterwards.
	const [position, setPosition] = useState(() => positionBelow(anchorEl));

	useEffect(() => {
		const updatePosition = () => setPosition(positionBelow(anchorEl));

		window.addEventListener("scroll", updatePosition, true);
		window.addEventListener("resize", updatePosition);

		return () => {
			window.removeEventListener("scroll", updatePosition, true);
			window.removeEventListener("resize", updatePosition);
		};
	}, [anchorEl]);

	useEffect(() => {
		const handlePointerDown = (event: MouseEvent) => {
			// composedPath() so a click inside a shadow root is not mistaken for an outside click.
			const target = getComposedEventTarget(event);

			if (target && !menuRef.current?.contains(target) && !anchorEl.contains(target)) {
				onClose();
			}
		};

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				onClose();
			}
		};

		document.addEventListener("mousedown", handlePointerDown);
		document.addEventListener("keydown", handleKeyDown);

		return () => {
			document.removeEventListener("mousedown", handlePointerDown);
			document.removeEventListener("keydown", handleKeyDown);
		};
	}, [anchorEl, onClose]);

	return ReactDOM.createPortal(
		<div
			ref={menuRef}
			className="fixed z-[250] overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg"
			data-testid="sidebar-email-reconnect-menu"
			style={{ top: position.top, left: position.left, width: MENU_WIDTH }}
		>
			<div className="px-3 pt-2.5 pb-1 text-xs text-text-meta">
				{t("sidebar.chooseEmailToReconnect")}
			</div>
			{emails.map((email) => (
				<button
					key={email.id}
					className="block w-full truncate px-3 py-2.5 text-left text-sm text-gray-900 duration-200 hover:bg-brand-white cursor-pointer"
					title={email.email}
					type="button"
					onClick={() => onSelect(email.id)}
				>
					{email.email}
				</button>
			))}
		</div>,
		portalContainer ?? document.body
	);
};
